import { Plus } from "lucide-react";
import { formatDate } from "@/lib/format";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accountsApi,
  clientAddressesApi,
  type AccountSummaryDto,
  type CreateAccountRequest,
} from "@/lib/clients-accounts";
import { apiFetch } from "@/lib/api";
import { AccountStatusBadge, ClientPicker, type AccountStatusCode, type ClientPickerValue } from "@/components/clients-accounts";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn, type SortDirection } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailModal } from "@/components/detail-modal";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// View chips — single-select, drives backend filter query
// ---------------------------------------------------------------------------
// Maps to backend params on /api/accounts (status, overdueOnly, minBalance).
// The legacy "On hold" view is gated on AccountStatus.Hold which is a Plan II
// item and not wired yet — chip omitted until the status code is in use.

type ViewChipId = "all" | "open" | "overdue" | "balance" | "closed";

const VIEW_CHIP_OPTIONS: ReadonlyArray<{ id: ViewChipId; label: string }> = [
  { id: "all",     label: "All" },
  { id: "open",    label: "Open" },
  { id: "overdue", label: "Overdue" },
  { id: "balance", label: "Balance owing" },
  { id: "closed",  label: "Closed" },
];

function viewQs(view: ViewChipId): string {
  switch (view) {
    case "open":    return "status=Open";
    case "overdue": return "status=Overdue&overdueOnly=true";
    case "balance": return "minBalance=0.01";
    case "closed":  return "status=Closed";
    case "all":
    default:        return "";
  }
}

// ---------------------------------------------------------------------------
// DTOs for companies
// ---------------------------------------------------------------------------

interface CompanyOption { id: number; companyName: string; }
interface ClientAddressOption { id: number; formattedAddress: string | null; address: string; }

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// New Account drawer form
// ---------------------------------------------------------------------------

function NewAccountForm({
  onSubmit,
  pending,
  conflictMessage,
}: {
  onSubmit: (req: CreateAccountRequest) => void;
  pending: boolean;
  conflictMessage: string | null;
}) {
  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [companyId, setCompanyId] = useState<string>("");
  const [addressId, setAddressId] = useState<string>("");

  const { data: companies } = useQuery({
    queryKey: ["companies-list"],
    queryFn: () => apiFetch<{ items: CompanyOption[] }>("/api/companies?pageSize=500"),
    staleTime: 60_000,
  });

  const { data: addresses } = useQuery({
    queryKey: ["client-addresses-picker", client?.clientId],
    queryFn: () => clientAddressesApi.list(client!.clientId),
    enabled: client !== null,
  });

  // When client is selected, auto-fill company if the client has one
  function handleClientChange(v: ClientPickerValue | null) {
    setClient(v);
    setAddressId("");
    if (v?.companyId) {
      setCompanyId(String(v.companyId));
    } else {
      setCompanyId("");
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!client || !companyId || !addressId) return;
    onSubmit({
      clientId: client.clientId,
      companyId: Number(companyId),
      accountAddressId: Number(addressId),
    });
  }

  const activeAddresses = (addresses ?? []).filter((a) => !(a as ClientAddressOption & { deleted?: boolean }).deleted);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {conflictMessage && (
        <Badge variant="destructive" className="w-full text-wrap">{conflictMessage}</Badge>
      )}
      <div>
        <Label>Client *</Label>
        <ClientPicker value={client} onChange={handleClientChange} />
      </div>
      <div>
        <Label htmlFor="na-company">Company *</Label>
        <Select
          value={companyId}
          onValueChange={setCompanyId}
          disabled={!!(client?.companyId)}
        >
          <SelectTrigger id="na-company">
            <SelectValue placeholder="Select company" />
          </SelectTrigger>
          <SelectContent>
            {(companies?.items ?? []).map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>{c.companyName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {client?.companyId && (
          <p className="mt-1 text-xs text-muted-foreground">Auto-filled from selected client</p>
        )}
      </div>
      <div>
        <Label htmlFor="na-address">Address *</Label>
        <Select
          value={addressId}
          onValueChange={setAddressId}
          disabled={!client}
        >
          <SelectTrigger id="na-address">
            <SelectValue placeholder={client ? "Select address" : "Select a client first"} />
          </SelectTrigger>
          <SelectContent>
            {activeAddresses.map((a: ClientAddressOption) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.formattedAddress ?? a.address}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={pending || !client || !companyId || !addressId}>
        {pending ? "Creating…" : "Create account"}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// CSV export (client-side, current page only)
// ---------------------------------------------------------------------------

function exportCsv(rows: AccountSummaryDto[]) {
  const headers = ["Ref", "Company", "Client", "Status", "Balance", "Overdue", "Last payment", "Last stmt", "Next due", "Terms accepted"];
  const lines = rows.map((r) => [
    r.accountReferenceNo,
    r.companyName ?? "",
    r.clientFullName ?? "",
    r.accountStatusCode,
    r.currentBalanceOutstanding,
    r.totalOverdue ?? 0,
    r.lastPaymentDate ?? "",
    r.lastStatementIssuedDate ?? "",
    r.nextPaymentDueDate ?? "",
    r.termsAccepted ? "Yes" : "No",
  ].map(String).join(","));
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "accounts.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// AccountsListPage
// ---------------------------------------------------------------------------

export function AccountsListPage() {
  const [searchText, setSearchText] = useState("");
  const [selectedView, setSelectedView] = useState<ViewChipId>("all");
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const qc = useQueryClient();

  const parts = [`page=${page}&pageSize=50`];
  const v = viewQs(selectedView);
  if (v) parts.push(v);
  const trimmedSearch = searchText.trim();
  if (trimmedSearch) parts.push(`q=${encodeURIComponent(trimmedSearch)}`);
  const fullQs = parts.join("&");

  const { data, isLoading } = useQuery({
    queryKey: ["accounts", page, selectedView, trimmedSearch],
    queryFn: () => accountsApi.list(fullQs),
  });

  // Sort state — client-side only; accountsApi.list has no `sort` param.
  // Sort applies to the current page's rows, not the full result set.
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(null);

  const visibleRows = useMemo(() => {
    const rows = data?.items ?? [];
    if (!sort) return rows;
    const dir = sort.direction === "asc" ? 1 : -1;
    const valueFor = (r: AccountSummaryDto): string | number | null => {
      switch (sort.columnId) {
        case "ref":         return r.accountReferenceNo;
        case "company":     return r.companyName ?? null;
        case "client":      return r.clientFullName ?? null;
        case "status":      return r.accountStatusCode;
        case "balance":     return r.currentBalanceOutstanding;
        case "overdue":     return r.totalOverdue ?? null;
        case "lastPayment": return r.lastPaymentDate ?? null;
        case "lastStmt":    return r.lastStatementIssuedDate ?? null;
        case "nextDue":     return r.nextPaymentDueDate ?? null;
        case "terms":       return r.termsAccepted ? 1 : 0;
        default:            return null;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [data, sort]);

  const createMut = useMutation({
    mutationFn: (req: CreateAccountRequest) => accountsApi.create(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      setCreating(false);
      setConflictMessage(null);
      toast.success("Account created");
    },
    onError: async (err: unknown) => {
      // Try to extract a 409 problem-detail title
      if (err instanceof Response && err.status === 409) {
        try {
          const body = await err.json() as { title?: string };
          setConflictMessage(body.title ?? "Conflict — this client or company already has an account.");
        } catch {
          setConflictMessage("Conflict — this client or company already has an account.");
        }
      }
    },
  });

  const cols: DataGridColumn<AccountSummaryDto>[] = [
    {
      id: "ref",
      header: "Ref",
      sortable: true,
      accessor: (r) => (
        <Link to={`/accounts/${r.accountId}`} className="underline text-primary">
          {r.accountReferenceNo}
        </Link>
      ),
    },
    { id: "company", header: "Company", sortable: true, accessor: (r) => r.companyName ?? "—" },
    { id: "client",  header: "Client",  sortable: true, accessor: (r) => r.clientFullName ?? "—" },
    {
      id: "status",
      header: "Status",
      sortable: true,
      accessor: (r) => <AccountStatusBadge code={r.accountStatusCode as AccountStatusCode} />,
    },
    {
      id: "balance",
      header: "Balance",
      sortable: true,
      accessor: (r) => fmt.format(r.currentBalanceOutstanding),
      align: "right",
    },
    {
      id: "overdue",
      header: "Overdue",
      sortable: true,
      accessor: (r) => r.totalOverdue != null ? fmt.format(r.totalOverdue) : "—",
      align: "right",
    },
    { id: "lastPayment", header: "Last payment", sortable: true, accessor: (r) => r.lastPaymentDate ? formatDate(r.lastPaymentDate) : "—" },
    { id: "lastStmt",    header: "Last stmt",    sortable: true, accessor: (r) => r.lastStatementIssuedDate ? formatDate(r.lastStatementIssuedDate) : "—" },
    { id: "nextDue",     header: "Next due",     sortable: true, accessor: (r) => r.nextPaymentDueDate ? formatDate(r.nextPaymentDueDate) : "—" },
    { id: "terms",       header: "Terms",        sortable: true, accessor: (r) => r.termsAccepted ? "✓" : "—" },
  ];

  return (
    <ListPage
      title="Accounts"
      primaryAction={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => exportCsv(data?.items ?? [])}
            disabled={(data?.items ?? []).length === 0}
          >
            Export CSV
          </Button>
          <IntentButton onClick={() => { setCreating(true); setConflictMessage(null); }}>
            <Plus className="mr-1.5 h-4 w-4" /> Add Account
          </IntentButton>
        </div>
      }
      search={{
        value: searchText,
        onChange: (v) => { setSearchText(v); setPage(0); },
        placeholder: "Search accounts…",
      }}
      filterChips={{
        options: VIEW_CHIP_OPTIONS,
        value: selectedView,
        onChange: (next) => { setSelectedView(next); setPage(0); },
        "aria-label": "Account view filter",
      }}
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.accountId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
        onSortChange={setSort}
      />
      <DetailModal
        open={creating}
        onOpenChange={(o) => { if (!o) { setCreating(false); setConflictMessage(null); } }}
        title="New account"
      >
        <NewAccountForm
          onSubmit={(req) => createMut.mutate(req)}
          pending={createMut.isPending}
          conflictMessage={conflictMessage}
        />
      </DetailModal>
    </ListPage>
  );
}
